import styled from 'styled-components';
import { IconButton, Menu, MenuItem, Typography, Box, LinearProgress, Tooltip, Fade } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect, useCallback } from 'react';
import NotificationSection from './Notification';
import useRedux from 'redux/useRedux';
import { unsubscribeTokenFromTopic } from '../../../../firebase';
import { AuthLogout, switchMerchant } from 'redux/auth';
import ConfirmationModal from 'components/Modal/confirmation';
import { toast } from 'react-toastify';
import IcUserSwitch from '../../../../assets/icons/user-switch.svg';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import { getBusinessProfile } from 'redux/business';
import { getProductList } from 'redux/products/product.services';
import { updateMerchantData } from 'redux/auth/auth.reducers';
import ApiService from 'services/ApiService';
import { useOnboardingSafe } from 'context/onboarding.context';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

interface HeaderProps {
  sidebar: string;
  handleSidebar: any;
}

export default function Header({ sidebar, handleSidebar }: HeaderProps) {
  const {
    thunkDispatch,
    storeState: { Auth, Notification },
  } = useRedux();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
  const [switching, setSwitching] = useState(false);
  const [isPhoneIdEmpty, setIsPhoneIdEmpty] = useState(false);
  const userRole = Auth?.data?.data?.roles?.[0] ?? '';
  const navigate = useNavigate();
  const [productCount, setProductCount] = useState(0);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  const onboarding = useOnboardingSafe();

  useEffect(() => {
    const fetchConfig = async () => {
      const merchantId = Auth?.data?.data?.merchant?.id;
      const token = Auth?.data?.data?.token;
      if (merchantId && token) {
        try {
          const profileRes: any = await thunkDispatch(getBusinessProfile({ token, id: merchantId } as any));
          if (profileRes?.payload?.status === 'success' && profileRes.payload.data?.data) {
            thunkDispatch(updateMerchantData(profileRes.payload.data.data));
            const waNumber = profileRes.payload.data.data.whatsappNumber;
            const waActive = !!waNumber && String(waNumber).trim() !== '' && String(waNumber) !== '0';
            setIsPhoneIdEmpty(!waActive);
          }

          const prodRes: any = await thunkDispatch(getProductList({ token, merchant_id: merchantId, page_size: 5, page_no: 1 } as any));
          if (prodRes?.payload?.status === 'success') {
            setProductCount(prodRes.payload.data?.pageData?.totalElements || 0);
          }
        } catch (error) {
          console.error('Failed to fetch config data', error);
        }
      }
    };
    fetchConfig();
  }, [Auth?.data?.data?.merchant?.id, Auth?.data?.data?.token, thunkDispatch]);

  const MerchantIcon = ({ merchant: m }: { merchant: any }) => {
    const iconUrl = m?.foto;
    if (iconUrl) {
      return (
        <img
          src={iconUrl}
          alt={m?.businessName || 'merchant'}
          style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }}
        />
      );
    }
    return <ManageAccountsOutlinedIcon fontSize="medium" />;
  };

  const handleMerchantClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMerchantClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleUserClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchorEl(event.currentTarget);
  };
  const handleUserClose = useCallback(() => {
    setUserAnchorEl(null);
  }, []);
  const handleSwitchMerchant = useCallback(
    async (merchantId: number, showToast: boolean = true) => {
      if (switching) return;
      setSwitching(true);
      try {
        const result = await thunkDispatch(switchMerchant(merchantId)).unwrap();
        if (result.status === 'success') {
          if (showToast) toast.success('Merchant switched successfully');
        } else {
          if (showToast) toast.error('Failed to switch merchant');
        }
      } catch (error) {
        if (showToast) toast.error('An error occurred while switching merchant');
      } finally {
        setSwitching(false);
        handleMerchantClose();
      }
    },
    [switching, thunkDispatch, handleMerchantClose],
  );

  const handleLogout = () => {
    unsubscribeTokenFromTopic(
      Notification?.firebaseToken,
      Auth?.data?.data?.merchant?.merchantCode,
    );
    setTimeout(() => {
      thunkDispatch(AuthLogout())
        .unwrap()
        .then(() => {
          setConfirmOpen(false);
        });
    }, 3000);
  };
  useEffect(() => {
    const merchants = Auth?.data?.data?.merchants;
    const currentMerchant = Auth?.data?.data?.merchant;

    if (merchants && Array.isArray(merchants) && merchants.length > 0 && !currentMerchant) {
      const firstMerchant = merchants[0];
      if (firstMerchant && firstMerchant.id) {
        handleSwitchMerchant(firstMerchant.id, false);
      }
    }
  }, [Auth?.data?.data?.merchants, Auth?.data?.data?.merchant, handleSwitchMerchant]);

  let completionPercentage = 0;
  const missingItems: string[] = [];
  const currentMerchantData: any = Auth?.data?.data?.merchant;

  if (currentMerchantData) {
    const waNumber = currentMerchantData?.whatsappNumber;
    if (!!waNumber && String(waNumber).trim() !== '' && String(waNumber) !== '0') {
      completionPercentage += 30;
    } else {
      missingItems.push("Nomor WhatsApp AI (+30%)");
    }

    if (currentMerchantData?.foto && String(currentMerchantData.foto).trim() !== '') {
      completionPercentage += 10;
    } else {
      missingItems.push("Foto Profil Bisnis (+10%)");
    }

    if (currentMerchantData?.address && String(currentMerchantData.address).trim() !== '') {
      completionPercentage += 20;
    } else {
      missingItems.push("Alamat Bisnis (+20%)");
    }

    if (currentMerchantData?.postalCode && String(currentMerchantData.postalCode).trim() !== '') {
      completionPercentage += 10;
    } else {
      missingItems.push("Kode Pos (+10%)");
    }

    // Hitung produk menggunakan state productCount yang di fetch
    const currentProductCount = productCount || currentMerchantData?.totalProduct || currentMerchantData?.productCount || 0;
    if (currentProductCount >= 5) {
      completionPercentage += 30;
    } else {
      missingItems.push(`Minimal 5 Produk (Baru ${currentProductCount}) (+30%)`);
    }
  }

  useEffect(() => {
    let start = 0;
    const end = completionPercentage;
    if (end === 0) {
      setAnimatedPercentage(0);
      return;
    }

    const duration = 1500;
    const interval = 20;
    const step = end / (duration / interval);

    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        setAnimatedPercentage(end);
        clearInterval(timer);
      } else {
        setAnimatedPercentage(Math.floor(start));
      }
    }, interval);

    return () => clearInterval(timer);
  }, [completionPercentage]);

  return (
    <HeaderContainer sidebar={sidebar}>
      <div className="flex items-center gap-4">
        <IconButton onClick={handleSidebar} className="ms-2">
          <FontAwesomeIcon icon={faBars} />
        </IconButton>

      </div>

      <nav className="">
        <ul className="nav">
          {Auth?.data?.data?.merchants && Auth.data.data.merchants.length > 0 && (
            <li className="nav-item">
              <IconButton
                onClick={handleMerchantClick}
                sx={{
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  bgcolor: 'transparent !important',
                  '&:hover': {
                    bgcolor: 'transparent !important',
                    color: '#000',
                  },
                }}
              >
                <MerchantIcon merchant={Auth.data.data.merchant} />
                <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>
                  {Auth.data.data.merchant?.businessName}
                </Typography>
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMerchantClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {Auth.data.data.merchants.map((merchant) => (
                  <MenuItem
                    key={merchant.id}
                    onClick={() => handleSwitchMerchant(merchant.id)}
                    selected={merchant.id === Auth.data.data.merchant?.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '200px',
                      bgcolor: 'transparent !important',
                      color: merchant.id === Auth.data.data.merchant?.id ? '#000' : '#757575',
                      '&:hover': {
                        bgcolor: 'transparent !important',
                        color: '#000',
                      },
                    }}
                  >
                    <MerchantIcon merchant={merchant} />
                    <Typography variant="body2">{merchant.businessName}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </li>
          )}

          {userRole !== 'ROLE_SUPER_ADMIN' && completionPercentage < 100 && (
            <li className="nav-item" style={{ display: 'flex', alignItems: 'center' }}>
              {/* Kelengkapan Profil Progress - Right Side */}
              <Tooltip
                title={
                  missingItems.length > 0 ? (
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Profil yang belum lengkap:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                        {missingItems.map((item, idx) => (
                          <li key={idx} style={{ marginBottom: '4px' }}>{item}</li>
                        ))}
                      </ul>
                    </Box>
                  ) : "Profil Bisnis Lengkap!"
                }
                placement="bottom-end"
                TransitionComponent={Fade}
                arrow
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    minWidth: '260px',
                    mr: 1,
                    cursor: 'pointer',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&:hover .MuiLinearProgress-root': {
                      height: 12,
                    }
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    Kelengkapan Profil
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <style>{`
                      @keyframes progressShine {
                        0% { background-position: 200% center; }
                        100% { background-position: -200% center; }
                      }
                    `}</style>
                    <LinearProgress
                      variant="determinate"
                      value={animatedPercentage}
                      color={animatedPercentage === 100 ? 'success' : 'primary'}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        transition: 'height 0.3s ease',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          transition: 'transform 0.1s linear',
                          backgroundImage: animatedPercentage < 100
                            ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)'
                            : 'none',
                          backgroundSize: '200% 100%',
                          animation: animatedPercentage < 100 ? 'progressShine 2s linear infinite' : 'none',
                        }
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: animatedPercentage === 100 ? 'success.main' : 'text.primary', minWidth: '32px' }}>
                    {animatedPercentage}%
                  </Typography>
                </Box>
              </Tooltip>
            </li>
          )}

          <li className="nav-item">
            <NotificationSection
              notifications={userRole !== 'ROLE_SUPER_ADMIN' ? Notification.notifications : undefined}
              counter={userRole !== 'ROLE_SUPER_ADMIN' ? Notification.notificationCount : undefined}
              userRole={userRole}
            />
          </li>

          <li className="nav-item">
            <IconButton
              onClick={handleUserClick}
              sx={{
                marginRight: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                bgcolor: 'transparent !important',
                '&:hover': {
                  bgcolor: 'transparent !important',
                  color: '#000',
                },
              }}
            >
              <PersonOutlineOutlinedIcon fontSize="medium" sx={{ color: 'rgba(0, 0, 0, 0.5)' }} />
            </IconButton>
            <Menu
              anchorEl={userAnchorEl}
              open={Boolean(userAnchorEl)}
              onClose={handleUserClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {userRole !== 'ROLE_SUPER_ADMIN' && onboarding && !onboarding.isActive && (
                <MenuItem
                  onClick={() => {
                    handleUserClose();
                    onboarding.restartTour();
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minWidth: '150px',
                    background: 'linear-gradient(135deg, #2ec291, #1a9b74)',
                    color: '#ffffff',
                    fontWeight: 600,
                    borderRadius: '8px',
                    margin: '8px',
                    boxShadow: '0 2px 10px rgba(46,194,145,0.3)',
                    letterSpacing: 0.2,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2bb184, #178a67)',
                      color: '#ffffff',
                    },
                  }}
                >
                  <PlayArrowRoundedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Tour Tips</Typography>
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  handleUserClose();
                  navigate('/setting/change-password');
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '150px',
                  bgcolor: 'transparent !important',
                  color: '#757575',
                  '&:hover': {
                    bgcolor: 'transparent !important',
                    color: '#000',
                  },
                }}
              >
                <Typography variant="body2">Ubah Kata Sandi</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleUserClose();
                  setConfirmOpen(!confirmOpen);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '150px',
                  bgcolor: 'transparent !important',
                  color: '#757575',
                  '&:hover': {
                    bgcolor: 'transparent !important',
                    color: '#000',
                  },
                }}
              >
                <Typography variant="body2">Keluar</Typography>
              </MenuItem>
            </Menu>
          </li>
        </ul>
      </nav>
      <ConfirmationModal
        title="Konfirmasi"
        titleStyle={{ fontWeight: 'bold' }}
        description="Apakah yakin untuk keluar?"
        open={confirmOpen}
        handleClose={() => setConfirmOpen(!confirmOpen)}
        handleConfirm={() => handleLogout()}
      ></ConfirmationModal>
    </HeaderContainer>
  );
}

const HeaderContainer = styled.header<{ sidebar: string }>`
  position: sticky;
  top: 0;
  height: 60px;
  width: ${(p) => (p.sidebar === 'true' ? 'calc(100%-250px)' : 'calc(100%-5rem)')};
  margin-left: ${(p) => (p.sidebar === 'true' ? '250px' : '5rem')};
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: margin-left 0.4s ease-in, width 0.4s ease-in;
  border-bottom: 1px solid #dee2e6;
  z-index: 10000;
`;
